import Layout from '@/container/Full'
import { store } from '@/store/store'
import { accountPath } from '@/consts/email'
const EmailListView = () => import(/* webpackChunkName: "email" */ '../views/email/EmailListView.vue')
const EmailAccountView = () => import(/* webpackChunkName: "email" */ '../views/email/EmailAccountView.vue')
const AccountMailboxesView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountMailboxesView.vue')
const AccountForwardersView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountForwardersView.vue')
const AccountAutorespondersView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountAutorespondersView.vue')
const AccountFiltersView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountFiltersView.vue')
const AccountFiltersManageView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountFiltersManageView.vue')
const AccountDomainSettingsView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountDomainSettingsView.vue')
const AccountSpamView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountSpamView.vue')
const AccountMailboxSectionView = () => import(/* webpackChunkName: "email" */ '../views/email/account/AccountMailboxSectionView.vue')

/**
 * Mailboxes used to have a page of their own, with Overview, Aliases, Client
 * Setup and Security tabs. That page was dropped (AM-1105): the last three are
 * now account tabs that take the mailbox as their last segment, and Overview
 * has no successor, so it lands on the account's mailbox list.
 */
const MAILBOX_SECTIONS = ['aliases', 'client-setup', 'security']

const legacyMailboxPath = (mainDomain, email, tab) => {
  return MAILBOX_SECTIONS.includes(tab)
    ? accountPath(mainDomain, tab, email)
    : accountPath(mainDomain, 'accounts')
}

/**
 * The oldest mailbox address, `/email/mailbox/:serviceId/:email`, was shared in
 * previews. The service has to be looked up to learn its main domain, which is
 * why this is a guard rather than a static `redirect`.
 */
const redirectLegacyMailbox = async (to, from, next) => {
  const { serviceId, email, tab } = to.params
  try {
    const service = await store.dispatch('emailHub/resolveService', serviceId)
    next(service ? legacyMailboxPath(service.domain, email, tab) : '/email')
  } catch (e) {
    console.warn('email router: unable to resolve a legacy mailbox link', e)
    next('/email')
  }
}

const mailboxSectionRoute = (section, pageTitle) => ({
  path: `${section}/:mailbox?`,
  name: `email-account-${section}`,
  component: AccountMailboxSectionView,
  meta: {
    pageTitle,
    mailboxSection: section,
    access_type: 'authorized'
  }
})

// Nested the same way as router/profile.js: Layout -> shell that owns the
// sub-navigation -> one child per tab, each repeating `access_type` because
// vue-router 3 reads meta from the deepest matched record rather than merging.
//
// Never link into these routes by hand — use `accountPath()` from
// @/consts/email, which owns the encoding rule.
const routes = [
  {
    path: '/email',
    component: Layout,
    children: [
      {
        path: '',
        name: 'email-list',
        component: EmailListView,
        meta: {
          breadcrumb: 'Email',
          pageTitle: 'Email',
          access_type: 'authorized'
        },
        children: []
      },
      // Ahead of the account routes so `mailbox` is never read as a domain.
      {
        path: 'mailbox/:serviceId/:email/:tab?',
        beforeEnter: redirectLegacyMailbox,
        meta: {
          access_type: 'authorized'
        }
      },
      {
        // The account's main domain (`service.domain`) addresses the account.
        path: ':maindomain',
        component: EmailAccountView,
        meta: {
          pageTitle: 'Email',
          access_type: 'authorized'
        },
        children: [
          {
            path: '',
            name: 'email-account',
            redirect: to => accountPath(to.params.maindomain, 'accounts'),
            meta: {
              access_type: 'authorized'
            }
          },
          {
            path: 'accounts',
            name: 'email-account-accounts',
            component: AccountMailboxesView,
            meta: {
              pageTitle: 'Email Accounts',
              access_type: 'authorized'
            }
          },
          // The mailbox page that used to live here, and its tabs.
          {
            path: 'accounts/:mailbox/:tab?',
            redirect: to => legacyMailboxPath(to.params.maindomain, to.params.mailbox, to.params.tab),
            meta: {
              access_type: 'authorized'
            }
          },
          mailboxSectionRoute('aliases', 'Email Aliases'),
          mailboxSectionRoute('client-setup', 'Email Client Setup'),
          mailboxSectionRoute('security', 'Email Security'),
          {
            path: 'forwarders',
            name: 'email-account-forwarders',
            component: AccountForwardersView,
            meta: {
              pageTitle: 'Email Forwarders',
              access_type: 'authorized'
            }
          },
          {
            path: 'autoresponders',
            name: 'email-account-autoresponders',
            component: AccountAutorespondersView,
            meta: {
              pageTitle: 'Email Autoresponders',
              access_type: 'authorized'
            }
          },
          {
            path: 'filters',
            name: 'email-account-filters',
            component: AccountFiltersView,
            meta: {
              pageTitle: 'Email Filters',
              access_type: 'authorized'
            }
          },
          {
            path: 'filters/:account',
            name: 'email-account-filters-manage',
            component: AccountFiltersManageView,
            meta: {
              pageTitle: 'Email Filters',
              access_type: 'authorized'
            }
          },
          {
            path: 'spam',
            name: 'email-account-spam',
            component: AccountSpamView,
            meta: {
              pageTitle: 'Spam Protection',
              access_type: 'authorized'
            }
          },
          {
            path: 'domain-settings',
            name: 'email-account-domain-settings',
            component: AccountDomainSettingsView,
            meta: {
              pageTitle: 'Domain Settings',
              access_type: 'authorized'
            }
          },
          // Deliverability listed every domain on the service with a page per
          // domain; Domain Settings shows the opened domain only (AM-1108).
          {
            path: 'deliverability/:domain?',
            redirect: to => accountPath(to.params.maindomain, 'domain-settings'),
            meta: {
              access_type: 'authorized'
            }
          },
        ]
      },
    ],
  }
]

export default routes
